/**
 * The flight scene's `device` phase: getting a renderer that actually works,
 * and saying which one it turned out to be.
 *
 * A sibling of `scene-boot.ts` rather than a block inside it for the reason
 * every other `scene-*.ts` here exists — that file sits on aislop's size
 * ceiling — but also because this is the only part of the boot that may
 * **replace** `host.renderer`, and that is worth being able to find.
 *
 * The policy is `three/renderer-mode.ts`'s and the retry is
 * `three/renderer-open.ts`'s; neither of those imports three.js. What is left
 * here is the three lines that could not be pure: build a `WebGPURenderer` for a
 * given mode, write the survivor back onto the host, and turn the outcome into
 * the two reports a player sees.
 */

import { rendererFactLabel, type RendererFacts, type RendererFallbackRecord } from "../three/renderer-mode.ts";
import {
  DEVICE_LOSS_RELOAD_DELAY_MS,
  deviceLossHandling,
  openRenderer,
  type ReloadTarget,
} from "../three/renderer-open.ts";
import { rememberRendererFallback, forgetRendererFallback } from "../three/renderer-plan.ts";
import type { DeviceLossInfo } from "../three/webgpu-support.ts";
import { installBootProbe } from "./boot-probe.ts";
import type { BootReporter } from "./boot-reporter.ts";
import type { BootHost } from "./scene-boot.ts";
import { buildRenderer, isUnsizedDrawingBuffer } from "./flight-view.ts";
import type { FlightSceneCallbacks } from "./flight-scene-api.ts";

/**
 * Brings up `host.renderer`, replacing it with a WebGL one if the WebGPU
 * attempt rejects, and resolves when a renderer is initialised.
 *
 * Rejects only when the fallback fails too — a machine with neither backend —
 * which `watchInit`'s own `catch` still turns into the error screen.
 *
 * **The replacement is written back onto `host` before anything else in the
 * boot runs**, and that is the whole safety argument for `SceneState.renderer`
 * being the one mutable field on an otherwise `readonly` record. At this moment
 * the only things that have ever touched the renderer are `setPixelRatio` and
 * the `appendChild` of its canvas: no material is compiled, no pipeline exists,
 * and a three.js scene graph is renderer-independent — so the swap costs
 * nothing and invalidates nothing. The frame loop starts strictly afterwards.
 *
 * The consequence for everything downstream: **read `state.renderer` at the
 * point of use, never cache it in a closure taken at construction.**
 * `applyTransparentSort`, `resizeViewport`, `scene-draw.ts` and
 * `scene-killcam.ts` all do, which is what makes the field safe to move at all;
 * a sibling that captured it once would keep drawing into the dead canvas.
 */
export async function openFlightRenderer(
  host: BootHost,
  reporter: BootReporter,
  callbacks: FlightSceneCallbacks,
): Promise<void> {
  // Before the device exists, because the probe wraps `GPUDevice.prototype` and
  // the boot's first pipeline must land inside the count. A no-op without
  // `?boot-report` (`boot-report.ts`), and here rather than in `scene-boot.ts`
  // because that file is on the size ceiling.
  installBootProbe(typeof location === "undefined" ? undefined : location.search);
  const opened = await openRenderer(
    host.rendererPlan,
    // The attempt is the renderer `flight-scene.ts` already built for this same
    // plan, not a fresh one. Building a second here is what produced the
    // black-screen incident of 2026-08-27 — two devices, two canvases, and the
    // live one shoved below the fold; see `openRenderer`'s own note.
    host.renderer,
    () => buildRenderer(host.container, host.effects.enhancedModels, "webgl"),
    {
      onFellBack: rememberRendererFallback,
      onClearStoredFallback: forgetRendererFallback,
    },
  );
  host.renderer = opened.renderer;
  reportRenderer(opened.facts, reporter, callbacks);
}

/**
 * What a healthy flight viewport looks like the moment the boot has sized it.
 *
 * Pure and DOM-free so it can be asserted without a browser: it is handed the
 * host's children, the live canvas, and that canvas's drawing-buffer size.
 *
 * Both fields were wrong during the 2026-08-27 black screen and either one alone
 * identifies it, which is the point — 5,106 green tests said nothing, because
 * every unit involved was individually correct and it was their *composition*
 * that put a dead canvas over a live one. A check on the assembled result is the
 * only kind that could have caught it.
 */
export interface ViewportHealth {
  /** Elements in the flight host that are not the live canvas. Any at all is a leak, and the first one is a black screen. */
  readonly strays: number;
  /** The live canvas still at three's never-sized 300x150 default — a buffer nothing ever measured. */
  readonly unsized: boolean;
  readonly healthy: boolean;
}

export function inspectViewport(
  hosted: readonly unknown[],
  live: unknown,
  bufferWidthPx: number,
  bufferHeightPx: number,
): ViewportHealth {
  const strays = hosted.filter((element) => element !== live).length;
  const unsized = isUnsizedDrawingBuffer(bufferWidthPx, bufferHeightPx);
  return { strays, unsized, healthy: strays === 0 && !unsized };
}

/** The one-line diagnostic for an unhealthy viewport, separated so the test can pin the words rather than a `console` call. */
export function viewportHealthMessage(health: ViewportHealth): string {
  const faults = [
    health.strays > 0 ? `${health.strays} stray element(s) stacked over the live canvas` : "",
    health.unsized ? "the live canvas is still at three's unsized 300x150 buffer" : "",
  ].filter((fault) => fault.length > 0);
  return `red-opus: the flight viewport booted unhealthy — ${faults.join("; ")}. The world will not be visible.`;
}

/**
 * Runs the check above against the real host and says so loudly if it fails.
 *
 * A warning rather than a throw: by the time this runs the game is otherwise
 * ready to fly, and turning a *possible* misdiagnosis into the error screen
 * would trade a rare bad picture for a certain lost sortie. The value is that a
 * black screen stops being a silent one — the console names the mechanism, and
 * a headed probe can read the verdict without measuring a single pixel.
 */
export function reportViewportHealth(host: BootHost): void {
  const canvas = host.renderer.domElement;
  const health = inspectViewport([...host.container.children], canvas, canvas.width, canvas.height);
  performance.mark(`fly-boot:viewport-${health.healthy ? "ok" : "unhealthy"}`);
  if (health.healthy) return;
  console.warn(viewportHealthMessage(health));
}

/**
 * The backend, told twice: once to the route (which passes it to PREFERENCES
 * and anything else that wants the fact) and once to the loading screen.
 *
 * **The loading screen is told only when the answer is not WebGPU.** A line
 * saying `RENDERER: WEBGPU` on every ordinary boot would be a diagnostic
 * printed at a player who has no decision to make about it; the line exists to
 * explain a picture that is about to be subtly different, so it appears exactly
 * when there is a difference to explain. The `onRenderer` callback fires either
 * way, because a screen that prints a fact wants the fact in both of its states.
 */
function reportRenderer(facts: RendererFacts, reporter: BootReporter, callbacks: FlightSceneCallbacks): void {
  callbacks.onRenderer?.(facts);
  if (facts.mode !== "webgpu") reporter.note(rendererFactLabel(facts));
  performance.mark(`fly-boot:renderer-${facts.mode}`);
}

/** The four side effects a mid-sortie device loss has, each injectable so the path can be driven without a GPU or a page to reload. */
export interface DeviceLossEffects {
  readonly remember?: (record: RendererFallbackRecord) => void;
  /**
   * Three states, not two: **omitted** takes the page's own `location`,
   * **`null`** says there is deliberately nowhere to reload (a worker, a test),
   * and an object is a recorder. Without the middle state a test could not
   * distinguish "no reload target" from "use the real one".
   */
  readonly reload?: ReloadTarget | null;
  readonly schedule?: (run: () => void, afterMs: number) => void;
  /** Left injectable purely so a test can assert the delay it was asked for rather than wait it out. */
  readonly delayMs?: number;
}

/** `location`, or nothing where there is none — a test, a worker. */
function pageReload(): ReloadTarget | undefined {
  return typeof location === "undefined" ? undefined : location;
}

/**
 * What a lost `GPUDevice` does: record WebGL for the next boot, tell the pilot
 * what he has lost, and reload into it.
 *
 * **The order matters and is asserted.** The record is written *before* the
 * status is reported and long before the reload, because a reload that arrived
 * without it would come straight back on WebGPU, lose the device again, and
 * loop. The delay is not cosmetic either: `onStatus("error", …)` is the only
 * notice a pilot gets that the sortie is gone, and it has to reach a frame.
 */
export function fallBackAfterDeviceLoss(
  info: DeviceLossInfo,
  callbacks: FlightSceneCallbacks,
  effects: DeviceLossEffects = {},
): void {
  const loss = deviceLossHandling(info.reason, info.message);
  (effects.remember ?? rememberRendererFallback)(loss.record);
  callbacks.onStatus("error", loss.message);
  const reload = effects.reload === undefined ? pageReload() : effects.reload;
  if (!reload) return;
  const schedule = effects.schedule ?? ((run, afterMs) => void setTimeout(run, afterMs));
  schedule(() => reload.reload(), effects.delayMs ?? DEVICE_LOSS_RELOAD_DELAY_MS);
}
